import { useEffect, useRef, useState } from 'react';
import { fetchUsers, update, Workday } from '@/api/workday';
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogFooter,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { TablePagination } from '@/components/ui/custom/table-pagination';
import { useExportCsv } from '@/hooks/use-export-csv';
import {
	AlertDialogAction,
	AlertDialogCancel,
} from '@radix-ui/react-alert-dialog';
import {
	keepPreviousData,
	Updater,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import {
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	PaginationState,
	SortingState,
	useReactTable,
} from '@tanstack/react-table';
import {
	AlertTriangle,
	LoaderCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { getColumns } from './columns';

export function WorkdayTable({
	searchQuery,
	setIsLoading,
	allUsers,
}: {
	searchQuery: string;
	setIsLoading: (loading: boolean) => void;
	allUsers: boolean;
}) {
	// * Query client ==========================================================
	const queryClient = useQueryClient();

	// * Navigation ============================================================
	const navigate = useNavigate();

	// * State ==================================================================
	const [sorting, setSorting] = useState<SortingState>([]);
	const [updateData, setUpdateData] = useState<Record<string, Workday>>({});
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 10,
	});

	// * Query ==================================================================
	const { data, isLoading, isError, isFetching } = useQuery({
		queryFn: async () => fetchUsers(searchQuery, allUsers, pagination),
		queryKey: ['workday', pagination, searchQuery, allUsers], //results cache
		staleTime: 30000,
		placeholderData: keepPreviousData,
	});
	const workdays = data?.allUsers ?? [];
	const total = data?.total ?? 0;
	const errors = data?.errors ?? [];

	// * Loading state =========================================================
	useEffect(() => {
		if (setIsLoading) {
			setIsLoading(isLoading);
		}
	}, [isLoading, setIsLoading]);

	// * Reset pagination when search query changes ===========================
	useEffect(() => {
		setPagination((prev) => ({ ...prev, pageIndex: 0 }));
	}, [searchQuery]);

	// * Dirty confirmation ======================================================
	const [dialogOpen, setIsDialogOpen] = useState(false);
	const [isDirty, setIsDirty] = useState(false);
	const isDirtyRef = useRef(false);
	const [nextPageIndex, setNextPageIndex] = useState<number | null>(null);
	const [navigationAttempt, setNavigationAttempt] = useState<string | null>(
		null,
	);

	// * Handle input change ====================================================
	const handleInputChange = (
	id: string,
	field: keyof Workday,
	value: any,
	row: Workday,
	) => {
		if (!id) return;

		setUpdateData((prev) => ({
			...prev,
			[id]: {
				...(prev[id] ?? row),
				[field]: value,
			},
		}));
		setIsDirty(true);
		isDirtyRef.current = true;
	};

	// * Manage dirty confirmation ===============================================
	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (isDirtyRef.current) {
				event.preventDefault();
				setNavigationAttempt(window.location.pathname);
			}
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, []);

	// * Setup mutate function to call our update API ===========================
	const { mutate } = useMutation({
		mutationFn: (workday: Workday[]) => update(workday),
		onSuccess: () => {
			toast.success(`Success! Record has been updated successfully `, {
				dismissible: true,
			});

			queryClient.invalidateQueries({ queryKey: ['workday'] });
		},
		onError: () => {
			toast.error(`Error! Record could not be updated.`, {
				dismissible: true,
			});
		},
	});

	// * Save data =============================================================
	const handleSaveRow = async (rowId: string) => {
		if (!updateData[rowId]) return;
		mutate([updateData[rowId]], {
			onSuccess: () => {
				setUpdateData((prev) => {
					const newData = { ...prev };
					delete newData[rowId]; // clear just the saved row from local changes
					return newData;
				});
			},
		});
		setIsDirty(false);
	};

	// * Save all data =========================================================
	const handleSaveAll = async () => {
		const updatedRows = Object.values(updateData);
		if (updatedRows.length === 0) return;
		mutate(updatedRows, {
			onSuccess: () => {
				setUpdateData({}); // clear all local edits after saving
			},
		});
		setIsDirty(false);
	};

	// * Handle page change ====================================================
	const handlePageChange = (
		updater: Updater<PaginationState, PaginationState>,
	) => {
		setPagination((prev) => {
			const newPagination =
				typeof updater === 'function' ? updater(prev) : updater;

			if (isDirty) {
				setNextPageIndex(newPagination.pageIndex);
				setIsDialogOpen(true); // Show dirty confirmation
				return prev;
			}
			return newPagination; // Allow page change
		});
	};

	// * Confirm page change ===================================================
	const confirmPageChange = () => {
		// Leave anyway
		setIsDialogOpen(false);
		setIsDirty(false);
		setUpdateData({});

		if (nextPageIndex != null) {
			setPagination((prev) => ({ ...prev, pageIndex: nextPageIndex }));
			setNextPageIndex(null);
		}
	};

	// * Initialize columns ====================================================
	const columns = getColumns(handleInputChange, handleSaveRow, updateData);

	// * Initialize table ======================================================
	const table = useReactTable({
		//TanStack table
		data: workdays,
		rowCount: total,
		columns: columns,
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		state: {
			// Use external react state instead of TanStacks own model
			sorting,
			pagination,
		},
		onPaginationChange: handlePageChange,
		manualPagination: true,
	});

	// * Export Csv ===========================================================
	const { exportCsv, isExporting } = useExportCsv();

	return (
		<>
			<div className="h-1/2 w-full">
				{errors.length > 0 && (
					<Card className="mb-2 rounded-md">
						<CardContent className="p-4">
							<div className="mb-2 flex gap-2">
								<AlertTriangle className="h-5 w-5 text-red-800" />
								<strong className="text-sm uppercase text-red-800">Warning</strong>
							</div>
							<ul className="list-disc space-y-1 pl-5 text-red-700">
								{errors.map((err: any) => (
									<li>
										{err.error} <strong>{err.databaseName}</strong>
									</li>
								))}
							</ul>
						</CardContent>
					</Card>
				)}
				<div className="mb-4 flex justify-between">
					<Button
						disabled={isExporting}
						onClick={() => {
							exportCsv('/Workday/export', { name: searchQuery, allUsers: allUsers });
						}}
						variant="outline"
					>
						Export
					</Button>
					<Button
						className="bg-blue-500 text-white"
						onClick={handleSaveAll}
						disabled={Object.keys(updateData).length === 0}
					>
						Save All
					</Button>
				</div>

				{isLoading ? (
					<div>
						<Skeleton className="mb-2 h-8 w-full" />
						<Skeleton className="mb-2 h-8 w-full" />
						<Skeleton className="mb-2 h-8 w-full" />
						<Skeleton className="mb-2 h-8 w-full" />
					</div>
				) : isError ? (
					<div>
						<p>error</p>
					</div>
				) : isFetching ? (
					<div className="flex items-center justify-center py-10">
						<LoaderCircle size={40} className="animate-spin" />
					</div>
				) : (
					<>
						<div className="rounded-md border">
							<Table>
								<TableHeader>
									{table.getHeaderGroups().map((headerGroup) => (
										<TableRow key={headerGroup.id}>
											{headerGroup.headers.map((header) => {
												return (
													<TableHead key={header.id}>
														{header.isPlaceholder
															? null
															: flexRender(
																	header.column.columnDef.header,
																	header.getContext(),
																)}
													</TableHead>
												);
											})}
										</TableRow>
									))}
								</TableHeader>
								<TableBody>
									{table.getRowModel().rows?.length ? (
										table.getRowModel().rows.map((row) => (
											<TableRow
											key={`${row.original.id}-${row.original.aimInstance}`}
											data-state={row.getIsSelected() && 'selected'}
											>
												{row.getVisibleCells().map((cell) => (
													<TableCell key={cell.id}>
														{flexRender(cell.column.columnDef.cell, cell.getContext())}
													</TableCell>
												))}
											</TableRow>
										))
									) : (
										<TableRow>
											<TableCell colSpan={columns.length} className="h-24 text-center">
												No results.
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</div>
					</>
				)}
				<TablePagination table={table} isFetching={isFetching} />
			</div>
			<AlertDialog open={dialogOpen} onOpenChange={setIsDialogOpen}>
				<AlertDialogContent>
					<p>You have unsaved changes! Do you want to leave ?</p>

					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setIsDialogOpen(false)}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction onClick={confirmPageChange}>Leave</AlertDialogAction>
						<AlertDialogAction
							onClick={() => {
								handleSaveAll();
								setIsDialogOpen(false);
								if (navigationAttempt) navigate(navigationAttempt);
							}}
						>
							Save Changes
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
